import React from 'react';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import { get } from 'lodash';
import { lifecycle, withProps, withState, withHandlers, compose, withPropsOnChange } from 'recompose';
import withPermissions from '../../../auth/utils/withPermissions';
import { isRefundUnavailableError, refundAppointment } from '../../../main/appointments/actions';
import withToggle from '../../../common/hoc/withToggle';
import {
  fetchSummary,
  refundOwnerSale,
  refundOwnerCustomCharge,
  refundOwnerSaleItem,
  refundInteracCharge,
  refundRecurringMembership,
} from '../actions';
import {
  isLoading,
  getDailySummary,
  getDailySummaryNumberOfTickets,
} from '../selectors';
import fetchOnUpdate from '../../../common/decorators/fetchOnUpdate';
import config from '../../../../config';
import { showReceipt } from '../../../customer/receipts/actions';
import { getActiveShop, getBarberUser, getActiveType, activeShopPaymentDepositsEnabled } from '../../../auth/selectors';
import withOwnerTypeAndId from '../../../common/hoc/withOwnerTypeAndId';
import withFetchSummaryHandler from '../hoc/withFetchSummaryHandler';
import withNotifications from '../../../common/withNotifications';
import { RudderstackTracker, CHARGE_REFUNDED } from '../../../../modules/infra/observability/rudderstack';

const mapStateToProps = state => ({
  shop: getActiveShop(state),
  user: state.auth.user,
  barber: getBarberUser(state),
  items: getDailySummary(state),
  numberOfTickets: getDailySummaryNumberOfTickets(state),
  timezone: state.auth.timezone,
  isLoading: isLoading(state),
  activeType: getActiveType(state),
  paymentDepositsEnabled: activeShopPaymentDepositsEnabled(state),
});

const mapDispatchToProps = {
  fetchSummary,
  refundOwnerSale,
  refundAppointment,
  refundOwnerCustomCharge,
  refundOwnerSaleItem,
  refundInteracCharge,
  refundRecurringMembership,
  showReceipt,
};

const getOwner = (props, shopId) => ({
  ownerId: props.ownerType === 'barber' ? props.ownerId : shopId,
  ownerType: (props.ownerType === 'barber' ? props.ownerType : 'shop'),
});

const refundHandler = compose(
  withNotifications,
  withHandlers({
    refund: props => (sale, refundOptions) => {
      const shopId = get(sale, 'shopId') || get(props, 'selected.items[0].shopId');
      const owner = getOwner(props, shopId);
      const refundAction = (() => {
        if (sale.paymentMethodType === 'interac_present') {
          return props.refundInteracCharge(shopId, sale, refundOptions, props.fetchSummary);
        }
        if (sale.type === 'sale') {
          return props.refundOwnerSale(owner.ownerId, owner.ownerType, sale.id, refundOptions);
        }
        if (sale.type === 'appointment') {
          return props.refundAppointment(sale.id, refundOptions);
        }
        if (sale.type === 'recurring_membership') {
          return props.refundRecurringMembership(props.ownerType, props.ownerId, sale.subscriptionId, sale.id);
        }
        return props.refundOwnerCustomCharge(owner.ownerId, owner.ownerType, sale.id);
      });

      return refundAction().then((response) => {
        props.setExpanded({});
        props.setSelected({});

        RudderstackTracker.getInstance().track(CHARGE_REFUNDED);

        if (response !== 'interac_present') {
          return props.fetchSummary();
        }

        return Promise.resolve();
      }).catch((error) => {
        if (isRefundUnavailableError(error)) {
          props.addNotification({
            level: 'error',
            title: 'Refund failed',
            message: (
              <div>
                Partial refunds can only be processed after payment is collected, which usually happens about an hour after the appointment end time.
                <span className="u-type--bold">
                  {' '}Learn more{' '}
                  <a
                    className="u-font-color-white u-type--underline"
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://getsquire.my.site.com/help/s/article/HowtorefundanappointmentortransactionShopBarber6239fde27c965"
                  >
                    here
                  </a>.
                </span>
              </div>
            ),
          });
        }
      });
    },

    refundSingleItem: props => (sale, item) => {
      const owner = getOwner(props);
      return props.refundOwnerSaleItem(owner.ownerId, owner.ownerType, sale.id, item)
        .then(() => {
          props.setExpanded({});
          props.setSelected({});
          return props.fetchSummary();
        });
    },
  }),
);

const receiptHandler = withHandlers({
  getReceipt: props => sale => props.showReceipt(sale.chargeId, sale.totalCost, sale.customer),
});

const dateState = withState('currentDate', 'setCurrentDate', props =>
  moment()
    .tz(props.timezone)
    .format(config.dateFormatISO),
);

const convertPropsForFetch = withProps(props => ({
  params: {
    currentDate: props.currentDate,
    timezone: props.timezone,
    shop: props.shop ? props.shop.id : null,
  },
  actions: {
    fetchSummary: props.fetchSummary,
  },
  currentDate: props.currentDate || moment().tz(props.timezone).format(config.dateFormatISO),
  endDate: moment().tz(props.timezone).format(config.dateFormatISO),
}));

const enhance = compose(
  withPermissions,
  connect(mapStateToProps, mapDispatchToProps),
  withOwnerTypeAndId,
  dateState,
  withFetchSummaryHandler,
  withState('selected', 'setSelected', {}),
  withState('expanded', 'setExpanded', {}),
  refundHandler,
  receiptHandler,
  convertPropsForFetch,
  withPropsOnChange(['currentDate'], (props) => {
    props.setExpanded({});
    props.setSelected({});
  }),
  fetchOnUpdate(['currentDate', 'shop', 'barber', 'timezone'], (params, actions) =>
    actions.fetchSummary(),
  ),
  withToggle('refundModal'),
  withToggle('bookingDeposits'),
  withToggle('dailyTotalsModal'),
  lifecycle({
    componentDidUpdate(prevProps) {
      // refresh summary after closing daily totals to reflect potential tip payouts
      if (prevProps.dailyTotalsModalIsOn && !this.props.dailyTotalsModalIsOn) {
        this.props.actions.fetchSummary();
      }
    },
  }),
);

export default enhance;
